import type { ProductSearchHitLike } from './product-utils-plp';
import { normalizeErpPieceUnitOfMeasure } from './erp-unit-of-measure';

type ValueRecord = Record<string, unknown>;

/**
 * Reads a SCAPI custom attribute (`c_`-prefixed). Search hits expose these under
 * `representedProduct`; the PDP `Product` and enriched cart items carry them at
 * the root. Prefer `representedProduct`, then fall back to the root.
 */
function readCustomAttribute(product: ProductSearchHitLike, key: string): unknown {
    const rep = product?.representedProduct as ValueRecord | undefined;
    if (rep && rep[key] != null) return rep[key];
    return (product as ValueRecord)[key];
}

function coerceToTrimmedString(v: unknown): string | undefined {
    if (typeof v === 'string') {
        const s = v.trim();
        return s.length ? s : undefined;
    }
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return undefined;
}

function coerceToPositiveNumber(v: unknown): number | undefined {
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
    return undefined;
}

export interface ProductTileUnitRow {
    /** Weight used for the price-per-unit calculation, shown verbatim from its editorial PIM field. */
    weightText?: string;
    /** Unit of measure from `pimUm` (e.g. `kg`, `l`), when a price-per-unit line applies. */
    um?: string;
    /** Selling price divided by the selected numeric PIM weight, when a price-per-unit line applies. */
    pricePerUmValue?: number;
}

export type GetProductTileUnitRowOptions = {
    /** Selling unit price (after discount when applicable). */
    effectiveUnitPrice?: number;
    /**
     * Multipack pack count. When > 1, €/um = price / (packQty × selectedWeight)
     * (total weight of all packs in the multipack).
     */
    packQty?: number;
    /** ERP piece unit of measure from site preferences. */
    pieceUnitOfMeasure?: string;
};

/**
 * Resolves the tile/PDP weight and price-per-unit-of-measure row.
 *
 * - When `pimDrainedNetWeightNotOil` is true, `weightText` comes from
 *   `pimDrainedNetWeightDesc` and the numeric denominator is `pimDrainedNetWeight`.
 * - Otherwise, `weightText` comes from `pimEtiNetWeight` and the numeric denominator
 *   is `pimNetWeight`. Editorial weight values are shown as-is (no parsing/normalization).
 * - `um` + `pricePerUmValue` are only produced when `pimUm` is present and not
 *   the site's piece unit, the selected weight is a positive number, and the selling
 *   price is positive.
 *   The value is `price / (packQty × selectedWeight)` — no unit-of-measure conversion.
 *   `packQty` defaults to 1 (single unit).
 *
 * @param options.effectiveUnitPrice — selling unit price (after discount when applicable)
 * @param options.packQty — multipack quantity (defaults to 1)
 * @param options.pieceUnitOfMeasure — market-specific ERP piece unit from site preferences
 */
export function getProductTileUnitRow(
    product: ProductSearchHitLike,
    options?: GetProductTileUnitRowOptions
): ProductTileUnitRow {
    const useDrainedWeight = readCustomAttribute(product, 'c_pimDrainedNetWeightNotOil') === true;
    const weightText = coerceToTrimmedString(
        readCustomAttribute(product, useDrainedWeight ? 'c_pimDrainedNetWeightDesc' : 'c_pimEtiNetWeight')
    );
    const um = coerceToTrimmedString(readCustomAttribute(product, 'c_pimUm'));
    const selectedWeight = coerceToPositiveNumber(
        readCustomAttribute(product, useDrainedWeight ? 'c_pimDrainedNetWeight' : 'c_pimNetWeight')
    );
    const price = options?.effectiveUnitPrice;
    const packQty = options?.packQty != null && options.packQty > 1 ? options.packQty : 1;
    const pieceUnitOfMeasure = normalizeErpPieceUnitOfMeasure(options?.pieceUnitOfMeasure);

    if (!um || um.toUpperCase() === pieceUnitOfMeasure || selectedWeight == null || price == null || price <= 0) {
        return { weightText };
    }

    return {
        weightText,
        um,
        pricePerUmValue: price / (packQty * selectedWeight),
    };
}
